import { nanoid } from "nanoid/non-secure";
import React, { ClassAttributes, HTMLAttributes, PropsWithChildren, useEffect, useState } from "react";
import Select, { Props } from "react-select";
import { GroupBase } from "react-select";

import { IconProp } from "@fortawesome/fontawesome-svg-core";

// config
import { getCustomStyles } from "./Config";
// components
import { Control, Dropdown, DropdownIndicator, PopoutButton } from "./components/index";

// extend Select props to custom select (https://react-select.com/typescript#custom-select-props)
declare module "react-select/dist/declarations/src/Select" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
		clearInputValue?: () => void;
	}
}

export interface PopoutOption {
	label: string;
	value: string;
}

export type OptionProps = PropsWithChildren<{
	/**
	 * The ref to the inner div,
	 * needed for react-select interactivity
	 */
	innerRef: (instance: HTMLDivElement | null) => void;
	/**
	 * The props to pass to the inner div,
	 * needed for react-select interactivity
	 */
	innerProps: HTMLAttributes<HTMLDivElement>;
	selectProps: Props;
	/**
	 * The data for the option, useful if you want
	 * to render something derived from the label/value
	 */
	data: PopoutOption;
}>;

export type OptionComponent = React.FC<PropsWithChildren<ClassAttributes<OptionProps>>>;

/**
 * We must provide the component with a healthy and balanced diet of props
 */
interface PopoutSelectProps {
	/**
	 * The options to display in the select
	 * @required
	 */
	options: PopoutOption[];
	/**
	 * The function to call when the select value changes
	 */
	onChange?: (value: string) => void;
	/**
	 * Custom option component to use that will be passed to react-select
	 */
	optionComponent?: OptionComponent;
	/**
	 * Whether or not to show the search bar
	 * @default true
	 */
	showSearch?: boolean;
	/**
	 * The icon or text string to use for the button that opens the input
	 * only used if no children are provided
	 */
	icon?: IconProp;
	/**
	 * Whether or not to close the menu on select
	 */
	closeMenuOnSelect?: boolean;
	/**
	 * The id to use for the input element
	 */
	inputId?: string;
	/**
	 * close the menu from outside via prop
	 */
	closed?: boolean;
}

/**
 * An implementation of a Popout Select from React Select that can be used to match our design system
 * @param {PopoutSelectProps} props
 */
const PopoutSelect = (props: PropsWithChildren<PopoutSelectProps>) => {
	const {
		options,
		onChange,
		optionComponent,
		showSearch = true,
		icon,
		children,
		closeMenuOnSelect = false,
		inputId = nanoid(),
		closed = true,
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const [input, setInput] = useState("");

	const handleSelectItem = (selectedItem) => {
		if (onChange) {
			onChange(selectedItem.value);
		}
	};

	const handleInputChange = (inputValue, { action }) => {
		// action type "input-change" means the value of the inputValue (search field) has changed
		// this overrides other behaviors, specifically the "menu-close" action, which
		// we handle within our custom Control component
		if (action === "input-change") {
			setInput(inputValue);
		}
	};

	// custom function to clear the input value from within the Control component
	const clearInputValue = () => {
		setInput("");
	};

	useEffect(() => {
		setIsOpen(!closed);
	}, [closed]);

	return (
		<Dropdown
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
				if (showSearch) {
					clearInputValue();
				}
			}}
			target={
				<PopoutButton icon={icon} open={isOpen} onClick={() => setIsOpen(!isOpen)} disabled={closeMenuOnSelect}>
					{children}
				</PopoutButton>
			}
		>
			<Select
				inputId={inputId}
				components={{
					// All components related to modifying the search/control input
					...(showSearch ? { Control } : {}),
					...(showSearch ? { DropdownIndicator } : {}),
					...(showSearch ? { IndicatorSeparator: null } : {}),
					// The optional option component
					...(optionComponent ? { Option: optionComponent } : {}),
				}}
				// triggers whent the user ineracts with the search field
				onInputChange={handleInputChange}
				// custom prop allows calling function from within via the Control component's props.selectProps
				clearInputValue={clearInputValue}
				// value the user has typed into the search field
				inputValue={showSearch ? input : ""}
				styles={getCustomStyles(showSearch)}
				backspaceRemovesValue={false}
				isClearable={false}
				controlShouldRenderValue={false}
				closeMenuOnSelect={closeMenuOnSelect}
				menuIsOpen={!closeMenuOnSelect}
				isSearchable={showSearch}
				onChange={handleSelectItem}
				options={options}
				placeholder="Search"
				tabSelectsValue={false}
			/>
		</Dropdown>
	);
};

export default PopoutSelect;
